import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin, ArrowUpRight, Hash, Ticket, X, ChevronLeft, ChevronRight, Images } from 'lucide-react';

/**
 * Trade Fairs — data-driven.
 * Each fair has an ISO endDate; anything with endDate >= today is Upcoming,
 * everything else falls into the Previous archive automatically.
 * When the fair is completed, if `gallery` has entries, they render instead
 * of the invitation poster.
 */
const FAIRS = [
  {
    id: 'matecia-2026',
    name: 'MATECIA 2026 \u2014 New Delhi',
    startDate: '2026-08-20',
    endDate: '2026-08-23',
    venue: 'Yashobhoomi (IICC), Dwarka',
    city: 'New Delhi',
    country: 'India',
    hall: 'Hall No. 1',
    stall: 'Stall No. A3A',
    participation: 'MATECIA 2026 is a major B2B exhibition for furniture, building materials, architecture, interior products, surfaces, hardware and related industries. It connects manufacturers and brands with architects, interior designers, dealers, distributors, builders, project consultants and buyers. Meet Rajdhani Crafts & Daughters at Hall No. 1, Stall No. A3A.',
    poster: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/n509qyif_Matecia.jpeg',
    posterAlt: 'MATECIA 2026 \u2014 Rajdhani Crafts & Daughters invitation poster',
    posterAspect: '720 / 894',
    registrationUrl: 'https://www.matecia.com/',
    gallery: []
  },
  {
    id: 'imm-india-2026',
    name: 'IMM India 2026 \u2014 New Delhi',
    startDate: '2026-09-01',
    endDate: '2026-09-03',
    venue: 'Yashobhumi (IICC), Dwarka',
    city: 'New Delhi',
    country: 'India',
    hall: 'Hall No. 2-A',
    stall: 'Booth No. A-61',
    participation: 'IMM India is the Indian edition of the internationally established IMM Cologne furniture fair \u2014 a focused B2B platform for furniture manufacturers, designers, retailers, hospitality buyers and interior specifiers. Meet Rajdhani Crafts & Daughters at Hall No. 2-A, Booth No. A-61 to explore current collections, materials and manufacturing capabilities.',
    poster: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/zxodn71u_WhatsApp%20Image%202026-08-20%20at%203.23.14%20PM.jpeg',
    posterAlt: 'IMM India 2026 \u2014 Rajdhani Crafts & Daughters invitation poster',
    posterAspect: '726 / 990',
    registrationUrl: 'https://www.imm-india.com/',
    gallery: []
  },
  {
    id: 'ihgf-spring-2026',
    name: '61st IHGF Delhi Fair \u2014 Spring 2026',
    startDate: '2026-02-14',
    endDate: '2026-02-18',
    venue: 'India Expo Centre & Mart',
    city: 'Greater Noida',
    country: 'India',
    hall: 'Hall 16',
    stall: 'Stall J-02/09',
    participation: 'RCD attended the 61st IHGF Delhi Fair (also referenced as the 61st EPCH / IHGF Delhi Fair), presenting current furniture, materials and manufacturing capabilities to designers, architects, hospitality professionals, procurement teams, buyers and industry partners. The five-day edition supported new international conversations and continuing export engagements.',
    poster: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/6ennzrxc_Delhi%20Fair%202026.jpeg',
    posterAlt: 'IHGF Delhi Fair 2026 signage outside India Expo Centre & Mart',
    gallery: [
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/6ennzrxc_Delhi%20Fair%202026.jpeg',                     caption: 'IHGF Delhi Fair 2026 signage \u2014 India Expo Centre & Mart, Greater Noida', alt: 'IHGF Delhi Fair 2026 signage at the fair entrance' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/t6t25g3t_WhatsApp%20Image%202026-08-19%20at%2012.17.07%20PM.jpeg', caption: 'IHGF Delhi Fair 2026 \u2014 arrival signage at the venue',                       alt: 'IHGF Delhi Fair 2026 outdoor letter signage' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/4q54gydb_WhatsApp%20Image%202026-08-19%20at%2012.17.00%20PM.jpeg', caption: 'RCD stall front \u2014 Rajdhani Crafts & Daughters, Stall J-02/09',              alt: 'Rajdhani Crafts & Daughters stall entrance at IHGF 2026' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/jqnihlo4_WhatsApp%20Image%202026-08-19%20at%2012.17.04%20PM.jpeg', caption: 'Materials, finishes & hospitality references \u2014 Taj, Hyatt, ITC, Alila, Marriott', alt: 'Materials sample wall with hospitality client logos' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/f82s1h1z_WhatsApp%20Image%202026-08-19%20at%2012.17.06%20PM.jpeg', caption: 'Stall interior \u2014 shelving, dining and lounge vignettes',                   alt: 'RCD stall interior with bookshelf, dining and lounge setup' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/yah0sgr5_WhatsApp%20Image%202026-08-19%20at%2012.17.09%20PM.jpeg', caption: 'Guest suite vignette \u2014 bedside cabinets, mirrors and headboard',            alt: 'Bedroom vignette with bedside cabinets and mirror wall' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/rs5calbq_WhatsApp%20Image%202026-08-19%20at%2012.17.11%20PM.jpeg', caption: 'Detail \u2014 materials, finishes and joinery samples on display',              alt: 'Close-up of the materials and finishes sample wall' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/dfo5md1q_WhatsApp%20Image%202026-08-19%20at%2012.16.53%20PM.jpeg', caption: 'Working lounge inside the stall with visiting buyers',                          alt: 'Working lounge area inside the RCD stall' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/l9ytw5n6_WhatsApp%20Image%202026-08-19%20at%2012.16.55%20PM.jpeg', caption: 'RCD team welcoming visitors at Stall J-02/09',                                   alt: 'RCD team with visitors at stall entrance J-02/09' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/5967mi9d_WhatsApp%20Image%202026-08-19%20at%2012.16.59%20PM.jpeg', caption: 'International buyers meeting the RCD team on the stall',                         alt: 'International buyers with RCD team inside the stall' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/h5sca7vn_WhatsApp%20Image%202026-08-19%20at%2012.16.48%20PM.jpeg', caption: 'RCD stall \u2014 wide view of the exhibit facade',                               alt: 'Wide view of Rajdhani Crafts & Daughters exhibit facade' }
    ]
  },
  {
    id: 'ihgf-autumn-2025',
    name: 'IHGF Delhi Fair \u2014 Autumn 2025',
    startDate: '2025-10-13',
    endDate: '2025-10-17',
    venue: 'India Expo Centre & Mart',
    city: 'Greater Noida',
    country: 'India',
    hall: 'Hall 15-A',
    stall: 'Stall No. 07/05',
    participation: 'RCD presented selected furniture, materials and manufacturing capabilities at the IHGF Delhi Fair \u2014 Autumn 2025 edition, opening conversations with retail buyers, architects, interior designers, hospitality professionals and international procurement teams across five days on the stall.',
    poster: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/59u781qi_Fair%20photo.jpeg',
    posterAlt: 'Rajdhani Crafts & Daughters stall at IHGF Delhi Fair \u2014 Autumn 2025',
    gallery: [
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/59u781qi_Fair%20photo.jpeg',                                    caption: 'RCD stall on Hall 15-A \u2014 opening day view',    alt: 'Rajdhani Crafts & Daughters stall at IHGF Autumn 2025' },
      { image: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1400&auto=format&fit=crop&q=85',                                              caption: 'Reception bench & lounge staged on the stall',       alt: 'Reception seating vignette' },
      { image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/lohc72ms_BEDSIDE%20A%20%288%29.jpg',                            caption: 'Bedside cabinets \u2014 hospitality suite reference', alt: 'Bedside cabinet under warm lighting' }
    ]
  }
];

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatDateRange = (start, end) => {
  const s = new Date(start), e = new Date(end);
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  const y = e.getFullYear();
  if (sameMonth) {
    return `${s.getDate()}\u2013${e.getDate()} ${monthNames[e.getMonth()]} \u00b7 ${y}`;
  }
  const sameYear = s.getFullYear() === e.getFullYear();
  const startPart = `${s.getDate()} ${monthNames[s.getMonth()]}${sameYear ? '' : ' ' + s.getFullYear()}`;
  const endPart = `${e.getDate()} ${monthNames[e.getMonth()]} \u00b7 ${y}`;
  return `${startPart} \u2013 ${endPart}`;
};

/* ---------------- Gallery Lightbox ---------------- */
const GalleryLightbox = ({ fair, index, onClose, onPrev, onNext }) => {
  const item = fair.gallery[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', onKey);
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = original;
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0d0a09]/95 flex flex-col"
      role="dialog"
      aria-modal="true"
      data-testid={`fair-lightbox-${fair.id}`}
    >
      {/* header */}
      <div className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-white/10">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#a67c3d]">Fair Gallery</p>
          <h3 className="font-serif text-lg md:text-2xl text-[#faf6f0] truncate">{fair.name}</h3>
        </div>
        <button
          onClick={onClose}
          data-testid="lightbox-close"
          aria-label="Close gallery"
          className="w-10 h-10 flex items-center justify-center text-[#faf6f0] hover:text-[#a67c3d] transition-colors"
        >
          <X size={22} />
        </button>
      </div>

      {/* body */}
      <div className="flex-1 min-h-0 relative flex items-center justify-center px-4 md:px-16 py-4">
        <button
          onClick={onPrev}
          data-testid="lightbox-prev"
          aria-label="Previous photo"
          className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 bg-[#faf6f0]/10 hover:bg-[#a67c3d] text-[#faf6f0] flex items-center justify-center transition-colors rounded-full"
        >
          <ChevronLeft size={22} />
        </button>

        <img
          key={item.image}
          src={item.image}
          alt={item.alt || item.caption}
          className="max-h-full max-w-full object-contain shadow-2xl"
          style={{ maxHeight: 'calc(100vh - 200px)' }}
        />

        <button
          onClick={onNext}
          data-testid="lightbox-next"
          aria-label="Next photo"
          className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 bg-[#faf6f0]/10 hover:bg-[#a67c3d] text-[#faf6f0] flex items-center justify-center transition-colors rounded-full"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {/* footer */}
      <div className="px-5 md:px-8 py-4 border-t border-white/10 flex items-center justify-between gap-6">
        <p className="text-sm md:text-base text-[#faf6f0]/80 leading-snug max-w-3xl">
          {item.caption}
        </p>
        <span className="text-xs tracking-[0.28em] uppercase text-[#a67c3d] flex-shrink-0">
          {String(index + 1).padStart(2, '0')} / {String(fair.gallery.length).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
};

/* ---------------- Thumbnail strip (compact preview) ---------------- */
const GalleryStrip = ({ fair, onOpen }) => {
  if (!fair.gallery || fair.gallery.length <= 1) return null;
  const thumbs = fair.gallery.slice(0, 6);
  const remaining = fair.gallery.length - thumbs.length;
  return (
    <div className="mt-5" data-testid={`gallery-strip-${fair.id}`}>
      <div className="grid grid-cols-6 gap-1.5">
        {thumbs.map((g, i) => (
          <button
            key={g.image}
            onClick={() => onOpen(i)}
            data-testid={`gallery-thumb-${fair.id}-${i}`}
            className="relative aspect-square overflow-hidden bg-[#1a1614] group focus:outline-none focus:ring-2 focus:ring-[#a67c3d]"
            aria-label={`Open photo ${i + 1} of ${fair.gallery.length}`}
          >
            <img
              src={g.image}
              alt={g.alt || g.caption}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            {i === thumbs.length - 1 && remaining > 0 && (
              <div className="absolute inset-0 bg-[#1a1614]/70 flex items-center justify-center text-[#faf6f0] text-sm md:text-base font-medium">
                +{remaining}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Cards ---------------- */
const UpcomingCard = ({ fair, onOpenGallery }) => (
  <article
    data-testid={`upcoming-fair-${fair.id}`}
    className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 bg-white border border-[#e0d6c5] overflow-hidden"
  >
    <div className="lg:col-span-3 relative bg-[#1a1614] flex items-center justify-center overflow-hidden" style={{ aspectRatio: fair.posterAspect || '4 / 5', maxHeight: '760px' }}>
      <img
        src={fair.poster}
        alt={fair.posterAlt || fair.name}
        loading="lazy"
        className="w-full h-full object-cover"
      />
      {fair.gallery && fair.gallery.length > 0 && (
        <button
          onClick={() => onOpenGallery(fair, 0)}
          data-testid={`upcoming-open-gallery-${fair.id}`}
          className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2 bg-[#1a1614]/85 backdrop-blur text-[#faf6f0] text-xs tracking-[0.2em] uppercase hover:bg-[#a67c3d] transition-colors"
        >
          <Images size={14} /> View Fair Gallery
          <span className="text-[#a67c3d] group-hover:text-[#faf6f0]">&middot; {fair.gallery.length}</span>
        </button>
      )}
    </div>

    <div className="lg:col-span-2 p-6 md:p-8 lg:p-10 flex flex-col">
      <h3 className="font-serif text-2xl md:text-3xl text-[#1a1614] leading-tight mb-5">
        {fair.name}
      </h3>

      <ul className="space-y-2.5 text-sm text-[#3a2f27] mb-6">
        <li className="flex gap-3">
          <CalendarDays size={16} className="text-[#a67c3d] flex-shrink-0 mt-0.5" />
          <span>{formatDateRange(fair.startDate, fair.endDate)}</span>
        </li>
        <li className="flex gap-3">
          <MapPin size={16} className="text-[#a67c3d] flex-shrink-0 mt-0.5" />
          <span>{fair.venue}, {fair.city} &middot; {fair.country}</span>
        </li>
        {fair.hall && (
          <li className="flex gap-3">
            <Hash size={16} className="text-[#a67c3d] flex-shrink-0 mt-0.5" />
            <span>{fair.hall}</span>
          </li>
        )}
        {fair.stall && (
          <li className="flex gap-3">
            <Ticket size={16} className="text-[#a67c3d] flex-shrink-0 mt-0.5" />
            <span>{fair.stall}</span>
          </li>
        )}
      </ul>

      <p className="text-sm text-[#4a3f36] leading-relaxed mb-6">
        {fair.participation}
      </p>

      <GalleryStrip fair={fair} onOpen={(i) => onOpenGallery(fair, i)} />

      <div className="mt-6 flex flex-wrap gap-4 items-center">
        {fair.registrationUrl && (
          <a
            href={fair.registrationUrl}
            target="_blank"
            rel="noreferrer"
            data-testid={`plan-visit-${fair.id}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a1614] text-[#faf6f0] hover:bg-[#a67c3d] transition-colors text-sm tracking-wide"
          >
            Plan Your Visit <ArrowUpRight size={15} />
          </a>
        )}
        <Link
          to="/contact"
          data-testid={`contact-fair-${fair.id}`}
          className="rcd-link inline-flex items-center gap-2 text-sm text-[#1a1614] font-medium"
        >
          Contact RCD &rarr;
        </Link>
      </div>
    </div>
  </article>
);

const PreviousCard = ({ fair, onOpenGallery }) => {
  const cover = (fair.gallery && fair.gallery[0]) ? fair.gallery[0].image : fair.poster;
  const coverAlt = (fair.gallery && fair.gallery[0]) ? fair.gallery[0].alt : fair.posterAlt;
  const hasGallery = fair.gallery && fair.gallery.length > 0;
  return (
    <article
      data-testid={`previous-fair-${fair.id}`}
      className="group bg-white border border-[#e0d6c5] overflow-hidden hover:shadow-lg transition-shadow flex flex-col"
    >
      <button
        onClick={() => hasGallery && onOpenGallery(fair, 0)}
        disabled={!hasGallery}
        className="relative w-full overflow-hidden bg-[#1a1614] text-left"
        style={{ aspectRatio: '16 / 10' }}
        data-testid={`previous-open-cover-${fair.id}`}
        aria-label={hasGallery ? 'Open fair gallery' : fair.name}
      >
        <img
          src={cover}
          alt={coverAlt || fair.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[900ms]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1614]/70 via-transparent to-transparent" />
      </button>

      <div className="p-6 md:p-7 space-y-3 flex-1 flex flex-col">
        <h3 className="font-serif text-xl md:text-2xl text-[#1a1614] leading-tight">
          {fair.name}
        </h3>
        <div className="flex flex-col gap-2 text-sm text-[#4a3f36]">
          <p className="flex gap-2"><CalendarDays size={16} className="text-[#a67c3d] flex-shrink-0 mt-0.5" /> <span>{formatDateRange(fair.startDate, fair.endDate)}</span></p>
          <p className="flex gap-2"><MapPin size={16} className="text-[#a67c3d] flex-shrink-0 mt-0.5" /> <span>{fair.city} &middot; {fair.country}</span></p>
        </div>
        <p className="text-sm text-[#4a3f36] leading-relaxed pt-1">{fair.participation}</p>

        {hasGallery && (
          <div className="pt-1 mt-auto">
            <button
              onClick={() => onOpenGallery(fair, 0)}
              data-testid={`view-gallery-${fair.id}`}
              className="rcd-link inline-flex items-center gap-1.5 text-sm text-[#1a1614] font-medium"
            >
              <Images size={14} className="text-[#a67c3d]" /> View Fair Gallery &rarr;
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

/* ---------------- Page ---------------- */
const TradeFairs = () => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const upcoming = useMemo(
    () => FAIRS.filter(f => f.endDate >= today).sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [today]
  );
  const previous = useMemo(
    () => FAIRS.filter(f => f.endDate < today).sort((a, b) => b.endDate.localeCompare(a.endDate)),
    [today]
  );

  const [lightbox, setLightbox] = useState(null); // { fair, index }
  const openGallery = useCallback((fair, index) => setLightbox({ fair, index }), []);
  const closeGallery = useCallback(() => setLightbox(null), []);
  const nextPhoto = useCallback(() => {
    setLightbox(lb => lb ? { ...lb, index: (lb.index + 1) % lb.fair.gallery.length } : lb);
  }, []);
  const prevPhoto = useCallback(() => {
    setLightbox(lb => lb ? { ...lb, index: (lb.index - 1 + lb.fair.gallery.length) % lb.fair.gallery.length } : lb);
  }, []);

  return (
    <div className="bg-[#faf6f0] pb-4">
      {/* Hero */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 border-b border-[#e0d6c5]" data-testid="tradefairs-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-5">RCD &middot; Trade Fairs</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#1a1614] leading-[1.05] max-w-4xl">
            Meet RCD <span className="italic text-[#a67c3d]">at the fair.</span>
          </h1>
          <p className="mt-8 max-w-3xl text-lg text-[#4a3f36] leading-relaxed">
            Explore where RCD is exhibiting next and revisit selected fairs where the company has presented its materials, manufacturing capabilities and work. Upcoming fairs use official invitation posters; completed fairs move into an archive supported by event photographs.
          </p>
        </div>
      </section>

      {/* Sticky in-page anchor nav */}
      <nav
        className="sticky top-24 md:top-28 z-30 bg-[#faf6f0]/95 backdrop-blur border-b border-[#e0d6c5]"
        data-testid="tradefairs-anchor-nav"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center gap-6 md:gap-10 text-xs md:text-sm tracking-[0.24em] uppercase">
          <a href="#upcoming-fairs" className="text-[#1a1614] hover:text-[#a67c3d] transition-colors">
            Upcoming Fairs
          </a>
          <span className="text-[#c9bfae]">&middot;</span>
          <a href="#previous-fairs" className="text-[#1a1614] hover:text-[#a67c3d] transition-colors">
            Previous Fairs
          </a>
        </div>
      </nav>

      {/* Upcoming */}
      <section id="upcoming-fairs" className="py-20 md:py-24 scroll-mt-40" data-testid="upcoming-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12 md:mb-14">
            <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-4">Upcoming Fairs</p>
            <h2 className="font-serif text-3xl md:text-5xl text-[#1a1614] leading-tight">
              Where to <span className="italic text-[#a67c3d]">meet us next.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#4a3f36] leading-relaxed">
              Visit RCD at an upcoming industry fair to explore materials, furniture and manufacturing capabilities, meet the team and begin a conversation around current or future requirements.
            </p>
          </div>

          {upcoming.length > 0 ? (
            <div className="space-y-8 md:space-y-10">
              {upcoming.map(f => <UpcomingCard key={f.id} fair={f} onOpenGallery={openGallery} />)}
            </div>
          ) : (
            <div
              className="bg-white border border-[#e0d6c5] p-10 md:p-14 text-center"
              data-testid="upcoming-empty-state"
            >
              <h3 className="font-serif text-2xl md:text-3xl text-[#1a1614] leading-tight mb-4">
                The next presentation <span className="italic text-[#a67c3d]">is being prepared.</span>
              </h3>
              <p className="text-[#4a3f36] max-w-2xl mx-auto mb-6">
                No new fair has been announced. Follow RCD or contact the team for upcoming exhibition and visit information.
              </p>
              <Link
                to="/contact"
                data-testid="upcoming-empty-contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a1614] text-[#faf6f0] hover:bg-[#a67c3d] transition-colors text-sm tracking-wide"
              >
                Contact RCD <ArrowUpRight size={15} />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Previous */}
      <section
        id="previous-fairs"
        className="py-16 md:py-20 bg-white border-t border-[#e0d6c5] scroll-mt-40"
        data-testid="previous-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-10 md:mb-14">
            <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-4">Previous Fairs</p>
            <h2 className="font-serif text-3xl md:text-5xl text-[#1a1614] leading-tight">
              The conversations that <span className="italic text-[#a67c3d]">continue after the fair.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#4a3f36] leading-relaxed">
              Selected photographs and information from fairs where RCD has presented its work, met industry partners and opened new conversations around furniture and manufacturing.
            </p>
          </div>

          {previous.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {previous.map(f => <PreviousCard key={f.id} fair={f} onOpenGallery={openGallery} />)}
            </div>
          )}
        </div>
      </section>

      {/* Closing enquiry */}
      <section className="py-20 md:py-24 bg-[#faf6f0] border-t border-[#e0d6c5]" data-testid="tradefairs-cta">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-4">Meet RCD</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1a1614] leading-tight mb-5">
            Continue the conversation <span className="italic text-[#a67c3d]">beyond the fair.</span>
          </h2>
          <p className="text-[#4a3f36] mb-8">
            If you are planning to attend an upcoming event&mdash;or would prefer to begin with a project brief&mdash;contact the RCD team directly.
          </p>
          <Link
            to="/contact"
            data-testid="tradefairs-start-conversation"
            className="inline-flex items-center gap-2 px-7 py-3 bg-[#1a1614] text-[#faf6f0] hover:bg-[#a67c3d] transition-colors text-sm tracking-wide"
          >
            Start a Conversation <ArrowUpRight size={15} />
          </Link>
        </div>
      </section>

      {lightbox && (
        <GalleryLightbox
          fair={lightbox.fair}
          index={lightbox.index}
          onClose={closeGallery}
          onPrev={prevPhoto}
          onNext={nextPhoto}
        />
      )}
    </div>
  );
};

export default TradeFairs;
